/**
 * IdeasView Component
 *
 * Main view for managing content ideas.
 */

import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppStore } from "@/core/store/useAppStore";
import { TabGroup, TabItem, TabGroupConfig } from "@/components/ui/TabGroup";
import { getStageByStatus } from "@/domains/PostCreator/utils/stageConfig";
import { PostStatus } from "@/core/types/postTypes";
import { ConfirmDialog } from "@/components/ui/ConfirmDialog";
import {
  useIdeasQuery,
  useCreateIdeaMutation,
  useDeleteIdeaMutation,
  useUpdateIdeaMutation,
  useDuplicateIdeaMutation,
} from "./hooks/useIdeas";
import { Idea, IdeaStatus } from "./types";
import { IDEA_STATUS_CONFIG } from "./ideaStatusConfig";
import { IdeaPostIt } from "./components/IdeaPostIt";
import { CreateIdeaModal } from "./components/CreateIdeaModal";
import { PageLayout, PageHeader, PageSection } from "@/components/PageLayout";
import { EntityCreateButton, ENTITY_CONFIG } from "@/core/entityConfig";
import { useTrailNavOptions } from "@/core/navigation/trail";
import { ShareIcon } from "@/components/icons";
import { ShareWithClientModal } from "@/domains/PostCreator/ShareWithClientModal";
import { CreateIdeaRequest } from "./types";

const STATUS_TABS: { id: IdeaStatus | "ALL"; label: string; dotColor?: string }[] = [
  { id: "ALL", label: "Tutti" },
  ...Object.entries(IDEA_STATUS_CONFIG).map(([status, config]) => ({
    id: status as IdeaStatus,
    label: config.label,
    dotColor: config.dot,
  })),
];

export const IdeasView: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { selectedClient } = useAppStore();
  const clientId = selectedClient?.id;
  const trailNavOptions = useTrailNavOptions(ENTITY_CONFIG.idea.plural);

  const activeTab = (searchParams.get("status") as IdeaStatus | "ALL") || "DRAFT";
  const setActiveTab = (tab: IdeaStatus | "ALL") => {
    setSearchParams(tab === "DRAFT" ? {} : { status: tab });
  };
  const [isCreatingIdea, setIsCreatingIdea] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    idea: Idea | null;
  }>({ isOpen: false, idea: null });

  const statusFilter = activeTab === "ALL" ? undefined : activeTab;
  const { data: ideas = [], isLoading, error } = useIdeasQuery(clientId, statusFilter, true, true);

  const createMutation = useCreateIdeaMutation(clientId || "");
  const deleteMutation = useDeleteIdeaMutation(clientId || "");
  const updateMutation = useUpdateIdeaMutation(clientId || "");
  const duplicateMutation = useDuplicateIdeaMutation(clientId || "");

  const handleCreateIdea = async (data: CreateIdeaRequest) => {
    const idea = await createMutation.mutateAsync(data);
    // Hand off straight into the full workspace page for the new idea.
    setIsCreatingIdea(false);
    navigate(`../ideas/${idea.id}`, trailNavOptions);
  };

  const handleIdeaClick = (idea: Idea) => {
    navigate(`../ideas/${idea.id}`, trailNavOptions);
  };

  const handleDelete = (idea: Idea) => {
    setDeleteConfirm({ isOpen: true, idea });
  };

  const handleStatusChange = async (idea: Idea, status: IdeaStatus) => {
    await updateMutation.mutateAsync({ ideaId: idea.id, data: { status } });
  };

  const handleDuplicate = async (idea: Idea) => {
    await duplicateMutation.mutateAsync(idea.id);
  };

  const handlePostsClick = (idea: Idea) => {
    if (idea.posts && idea.posts.length > 0) {
      const firstPost = idea.posts[0];
      const stage = getStageByStatus(firstPost.status as PostStatus);
      navigate(`../posts?stage=${stage}`);
    }
  };

  const handleDeleteConfirm = async () => {
    if (deleteConfirm.idea) {
      await deleteMutation.mutateAsync(deleteConfirm.idea.id);
    }
    setDeleteConfirm({ isOpen: false, idea: null });
  };

  const tabItems: TabItem[] = STATUS_TABS.map((tab) => ({
    id: tab.id,
    label: tab.label,
    icon: tab.dotColor ? <div className={`w-2 h-2 rounded-full ${tab.dotColor}`} /> : undefined,
  }));

  const tabConfig: TabGroupConfig = {
    variant: "navigation",
    size: "sm",
    layout: "horizontal",
    fullWidth: false,
    hasBackground: true,
  };

  if (!selectedClient) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-semibold text-gray-700 mb-2">
            No Client Selected
          </h2>
          <p className="text-gray-500">
            Please select a client to manage their ideas
          </p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout>
      <PageHeader>
        <PageHeader.Row>
          <TabGroup
            items={tabItems}
            activeId={activeTab}
            onTabChange={(id) => setActiveTab(id as IdeaStatus | "ALL")}
            config={tabConfig}
          />
          <PageHeader.Actions>
            <button
              onClick={() => setIsShareModalOpen(true)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-all duration-200 font-medium flex items-center gap-2 shadow-xs hover:shadow-md"
              title="Share ideas review link with client"
            >
              <ShareIcon className="w-4 h-4" />
              Share with Client
            </button>
            <EntityCreateButton
              entity="idea"
              onClick={() => setIsCreatingIdea(true)}
            />
          </PageHeader.Actions>
        </PageHeader.Row>
      </PageHeader>

      <PageSection.Canvas>
        {isLoading ? (
          <div className="flex items-center justify-center flex-1">
            <div className="text-gray-500">Loading ideas...</div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center flex-1">
            <div className="text-red-500">
              Error loading ideas: {error instanceof Error ? error.message : "Unknown error"}
            </div>
          </div>
        ) : ideas.length === 0 ? (
          <div className="flex flex-col items-center justify-center flex-1">
            <div className="text-gray-400 mb-4">
              <svg
                className="w-16 h-16"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
            </div>
            <p className="text-gray-500 mb-4">
              {activeTab === "ALL"
                ? "No ideas yet"
                : `No ${activeTab.toLowerCase()} ideas`}
            </p>
          </div>
        ) : (
          <div className="py-1">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
              {ideas.map((idea) => (
                <IdeaPostIt
                  key={idea.id}
                  idea={idea}
                  onClick={handleIdeaClick}
                  onStatusChange={handleStatusChange}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onPostsClick={handlePostsClick}
                  isUpdating={updateMutation.isPending}
                />
              ))}
            </div>
          </div>
        )}
      </PageSection.Canvas>

      {/* Create Modal */}
      <CreateIdeaModal
        isOpen={isCreatingIdea}
        onClose={() => setIsCreatingIdea(false)}
        onSubmit={handleCreateIdea}
        isLoading={createMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={deleteConfirm.isOpen}
        onClose={() => setDeleteConfirm({ isOpen: false, idea: null })}
        onConfirm={handleDeleteConfirm}
        title="Delete Idea"
        message={`Are you sure you want to delete "${deleteConfirm.idea?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />

      {/* Share with Client Modal */}
      <ShareWithClientModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        clientId={selectedClient.id}
        clientName={selectedClient.clientName}
        redirectPath={`/client/${selectedClient.id}/ideas`}
      />
    </PageLayout>
  );
};
